import { forEach, omit, omitBy } from "lodash";
import { useGrowl } from "PFApp/use_growl";
import { useResponseErrors } from "PFCore/helpers/use_response_errors";
import { useFiltersConfigUpdate } from "PFCore/hooks/queries/saas_admin/filters_config/use_filters_config_update";
import { Id } from "PFTypes";
import { FiltersConfig, FiltersConfigFormData } from "PFTypes/filters_config";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

type UseFiltersConfigFromSubmitReturn = {
  handleSubmit: (formValues: FiltersConfigFormData) => void;
  handleError: () => void;
  isLoading: boolean;
};

export const useFiltersConfigFormSubmit = (
  accountId: Id,
  target: string | null
): UseFiltersConfigFromSubmitReturn => {
  const { t } = useTranslation("saasAdmin");
  const { getFormattedErrors } = useResponseErrors();
  const growl = useGrowl();
  const { mutate, isLoading } = useFiltersConfigUpdate(accountId, target || "");

  const parseFormValuesToMutationPayload = useCallback((formValues): FiltersConfig => {
    const parseFiltersFamily = (family) =>
      formValues[family].reduce((acc, filter) => {
        const key = filter.name;
        acc[key] = omitBy(omit(filter, "name"), (value) => Array.isArray(value) && value.length === 0);
        return acc;
      }, {});

    return {
      fields: parseFiltersFamily("fields"),
      strings: parseFiltersFamily("strings"),
      numbers: parseFiltersFamily("numbers"),
      availability: formValues["availability"][0],
      children: formValues["children"][0]?.target
    };
  }, []);

  const handleFiltersConfigUpdate = useCallback(
    (formValues: FiltersConfigFormData) => {
      const payload = parseFormValuesToMutationPayload(formValues);
      mutate(payload, {
        onSuccess: () =>
          growl({
            message: t("filtersConfig.success")
          }),
        onError: ({ response }) => {
          const errors = getFormattedErrors(response.data);
          forEach(errors, (message, fieldName) => {
            growl({
              message: `${fieldName}:${message}`,
              kind: "error"
            });
          });
        }
      });
    },
    [getFormattedErrors, mutate, growl, t, parseFormValuesToMutationPayload]
  );

  const handleError = () =>
    growl({
      message: t("errors.notifyGrowl"),
      kind: "error"
    });

  return {
    handleSubmit: handleFiltersConfigUpdate,
    handleError,
    isLoading
  };
};
